import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Mail, Phone, Briefcase, GraduationCap, Calendar, Save, Plus, RotateCcw, BellRing } from "lucide-react";
import { colorForShift, shiftLabel } from "@/lib/shifts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { mdyDow } from "@/lib/dateFmt";
import NotificationPrefs from "@/components/NotificationPrefs";

const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat","Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function EmployeeSheet({
  open, onClose, employee, assignments, codes, canEdit, payPeriodId, onSchedulingChange,
}) {
  const [pattern, setPattern] = useState(Array(14).fill(null));
  const [saving, setSaving]   = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!open || !employee) return;
    (async () => {
      try {
        const { data } = await api.get(`/employees/${employee.id}/master-pattern`);
        setPattern(data.pattern || Array(14).fill(null));
      } catch { /* ignore */ }
      setEditing(false);
    })();
  }, [open, employee]);

  const empAssignments = (assignments || []).filter(a => a.employee_id === employee?.id);
  const totalHours = empAssignments.reduce((s, a) => s + (a.hours || 0), 0);
  const codeMap = new Map((codes || []).map(c => [c.code, c]));

  const workCodes = (codes || []).filter(c => c.type === "work");
  const specials  = (codes || []).filter(c => c.type !== "work");

  const setDay = (i, code) => setPattern(p => p.map((c, idx) => idx === i ? code : c));

  const savePattern = async () => {
    setSaving(true);
    try {
      await api.put(`/employees/${employee.id}/master-pattern`, { pattern });
      toast.success("Master pattern saved");
      setEditing(false);
      onSchedulingChange && onSchedulingChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save");
    }
    setSaving(false);
  };

  const applyReset = async () => {
    if (!payPeriodId) return;
    if (!window.confirm(`Overwrite ${employee.name}'s shifts in this pay period with their master pattern?`)) return;
    try {
      const { data } = await api.post("/schedule/reset-employee", {
        pay_period_id: payPeriodId, employee_id: employee.id, clear_first: true,
      });
      toast.success(`Applied master pattern (${data.applied} shifts)`);
      onSchedulingChange && onSchedulingChange();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const shiftsCount = pattern.filter(c => c).length;
  const fteFromPattern = +(shiftsCount / 10).toFixed(2);

  return (
    <TooltipProvider delayDuration={150}>
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto thin-scroll" data-testid="employee-sheet">
          <SheetHeader>
            <div className="flex items-center gap-3">
              <Avatar className="w-14 h-14">
                <AvatarImage src={employee?.avatar} />
                <AvatarFallback>{employee?.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <SheetTitle className="font-display text-xl">{employee?.name}</SheetTitle>
                <SheetDescription>{employee?.role_label || employee?.position}</SheetDescription>
              </div>
              <div className="ml-auto text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">FTE</div>
                <div className="font-display text-xl font-semibold tabular-nums text-blue-700">
                  {fteFromPattern.toFixed(1)}
                </div>
              </div>
            </div>
          </SheetHeader>

          <div className="mt-6 grid grid-cols-1 gap-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700"><Mail size={14} className="text-slate-400" />{employee?.email}</div>
            {employee?.phone && (
              <div className="flex items-center gap-2 text-slate-700"><Phone size={14} className="text-slate-400" />{employee.phone}</div>
            )}
            <div className="flex items-center gap-2 text-slate-700">
              <Briefcase size={14} className="text-slate-400" />{employee?.role_label || employee?.position || "—"}
            </div>
            <div className="flex items-start gap-2 text-slate-700">
              <GraduationCap size={14} className="text-slate-400 mt-0.5" />
              <div className="flex flex-wrap gap-1">
                {(employee?.departments_trained || []).length === 0 && <span className="text-slate-400">No departments</span>}
                {(employee?.departments_trained || []).map((d, i) => (
                  <span key={i} className="text-[11px] rounded-md bg-slate-100 border border-slate-200 px-1.5 py-0.5">{d}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-700"><Calendar size={14} className="text-slate-400" />{totalHours}h scheduled this period</div>
          </div>

          {/* Master pattern */}
          {canEdit && (
            <div className="mt-6 border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-medium">Master pattern</div>
                  <div className="text-xs text-slate-500">14-day repeating template. {shiftsCount} shifts.</div>
                </div>
                <div className="flex gap-2">
                  {!editing && (
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="master-edit">
                      Edit
                    </Button>
                  )}
                  {editing && (
                    <Button className="bg-blue-600 hover:bg-blue-700" size="sm" onClick={savePattern} disabled={saving} data-testid="master-save">
                      <Save size={14} className="mr-1" /> Save
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={applyReset} data-testid="reset-from-master">
                    <RotateCcw size={14} className="mr-1" /> Apply to period
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {pattern.map((code, i) => {
                  const s = code ? codeMap.get(code) : null;
                  const clr = colorForShift(s);
                  return (
                    <Popover key={i}>
                      <PopoverTrigger asChild>
                        <button
                          disabled={!editing}
                          data-testid={`pattern-slot-${i}`}
                          className={`rounded-md border p-1.5 text-center transition-colors ${clr.bg} ${clr.text} ${clr.border}
                                      ${editing ? "hover:ring-2 ring-blue-500 ring-offset-1 cursor-pointer" : "cursor-default"}`}
                        >
                          <div className="text-[9px] uppercase tracking-widest text-slate-500 leading-none">
                            {DOW[i]}
                          </div>
                          <div className="text-sm font-semibold mt-0.5 min-h-[16px]">
                            {code || <span className="text-slate-300">·</span>}
                          </div>
                        </button>
                      </PopoverTrigger>
                      {editing && (
                        <PopoverContent side="bottom" align="start" className="w-56 p-2">
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Pick a shift</div>
                          <div className="grid grid-cols-4 gap-1">
                            <button
                              onClick={() => setDay(i, null)}
                              className="rounded-md border p-1.5 text-xs bg-white border-slate-200 text-slate-500 hover:border-red-300"
                            >
                              Off
                            </button>
                            {workCodes.map(c => {
                              const cc = colorForShift(c);
                              return (
                                <Tooltip key={c.code}>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => setDay(i, c.code)}
                                      className={`rounded-md border p-1.5 text-sm font-semibold ${cc.bg} ${cc.text} ${cc.border} hover:ring-2 ring-blue-500 ring-offset-1`}
                                    >
                                      {c.code}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">{shiftLabel(c)}</TooltipContent>
                                </Tooltip>
                              );
                            })}
                            {specials.map(c => {
                              const cc = colorForShift(c);
                              return (
                                <button
                                  key={c.code}
                                  onClick={() => setDay(i, c.code)}
                                  className={`rounded-md border p-1.5 text-xs font-semibold ${cc.bg} ${cc.text} ${cc.border} hover:ring-2 ring-blue-500 ring-offset-1`}
                                >
                                  {c.code}
                                </button>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      )}
                    </Popover>
                  );
                })}
              </div>
            </div>
          )}

          {/* Current period shifts */}
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">This pay period</div>
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto thin-scroll pr-1">
              {empAssignments.length === 0 && <div className="text-sm text-slate-500">No shifts assigned.</div>}
              {empAssignments
                .slice()
                .sort((a, b) => a.date.localeCompare(b.date))
                .map(a => {
                  const shift = codeMap.get(a.shift_code);
                  const c = colorForShift(shift || { code: a.shift_code, color: "regular" });
                  return (
                    <div key={a.id} className={`rounded-lg border p-2 flex items-center justify-between ${c.bg} ${c.border}`}>
                      <div className="text-sm">
                        <div className="font-medium text-slate-800">{mdyDow(a.date)}</div>
                        <div className="text-xs text-slate-600">
                          {a.shift_label}
                          {shift?.start && shift?.end && ` · ${shift.start}–${shift.end}`}
                        </div>
                      </div>
                      <div className={`text-sm font-semibold ${c.text}`}>{a.shift_code}</div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Manager notification preference override */}
          {canEdit && employee?.id && (
            <div className="mt-6 border border-slate-200 rounded-xl p-3" data-testid="emp-notif-prefs">
              <div className="flex items-center gap-2 text-sm font-medium">
                <BellRing size={14} className="text-blue-600" /> Notifications for {employee.name?.split(" ")[0] || "this employee"}
              </div>
              <div className="mt-2">
                <NotificationPrefs userId={employee.id} showManagerHint compact />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
}
